// src/hooks/useUserProfile.ts
import useSWR from 'swr';
import { doc, getDoc } from 'firebase/firestore';
import { db, appId } from '../firebase';
import { Profile } from '../types';



/**
 * Firebaseからユーザープロフィールを取得する関数
 * @param uid - ユーザーID
 * @returns プロフィールデータ
 */
export const fetchUserProfile = async (uid: string): Promise<Profile> => {
    if (!uid) {
        return { nickname: '', birthday: '' } as Profile;
    }

    const ref = doc(db, `artifacts/${appId}/users/${uid}/userData/profile`);
    const snapshot = await getDoc(ref);

    return snapshot.exists()
        ? (snapshot.data() as Profile)
        : { nickname: '', birthday: '' } as Profile;
};

/**
 * useUserProfile フックの戻り値の型定義
 */
interface UseUserProfileResult {
    profile: Profile | null;
    isLoading: boolean;
    error: Error | null;
    mutate: (data?: Profile | Promise<Profile> | null, shouldRevalidate?: boolean) => Promise<Profile | undefined>;
}

/**
 * ユーザープロフィールをSWRで取得し管理するカスタムフック。
 *
 * @param userId - ユーザーのID (string | undefined)
 * @returns { profile: Profile | null, isLoading: boolean, error: Error | null, mutate: SWRMutateFunction }
 */
export const useUserProfile = (userId: string | undefined): UseUserProfileResult => {
    // useSWR のキーは userId が存在する場合のみ有効になるように設定
    const swrKey = userId ? ['user-profile', userId] : null;

    // useSWR を使用してデータをフェッチ
    const { data, error, isLoading, mutate } = useSWR<Profile>(
        swrKey,
        // フェッチャー関数は引数として key を受け取りますが、ここでは userId が必要なので、
        // クロージャーで userId を渡すか、キーから userId を抽出します。
        // fetchUserProfile は既に userId を引数に取るので、直接渡す形にします。
        // useSWR の fetcher には key を受け取る関数を渡すため、ここでは key[1] を userId として渡します。
        // TypeScript の型推論を助けるため、アロー関数で key を key: [string, string] と定義します。
        (key: [string, string]) => fetchUserProfile(key[1]),
        {
            revalidateOnFocus: false, // 画面再フォーカス時の再検証を無効化
            // エラー発生時の再試行回数など、SWR の設定をここに追加できます
            // onErrorRetry: (error, key, config, revalidate, { retryCount }) => { ... },
        }
    );

    return {
        profile: data || null, // データがない場合は null を返す
        isLoading,
        error,
        mutate, // SWR の mutate 関数をそのまま公開
    };
};